import {
  type DSFlexAlignItems,
  type DSGridSpacing,
  type DSIconColorType,
  type DSIconName,
  type DSIconSize,
  type DSSurfaceColorType,
  type DSTextAlignType,
  type DSTextColorType,
  DSDivider,
  DSFlex,
  DSSpacer,
  DSSurface,
  DSText,
  DSTextAndIcon,
} from '@zoe/ds-web'
import { isHeading, isLink, isParagraph, isThematicBreak } from 'datocms-structured-text-utils'
import type { ReactElement } from 'react'
import {
  type RenderBlockContext,
  type StructuredTextGraphQlResponse,
  StructuredText,
  renderNodeRule,
} from 'react-datocms'
import styled from 'styled-components'

import type { SimpleImageRecord, VideoRecord } from '@/types/datocms-schema'
import type { FragmentComponent } from '@/types/graphql'

import { responsiveImageBlock } from '@/graphql/utils/commonBlocks'

import { TrackedInlineLink } from '@/components/shared/TrackedInlineLink'

import { type AntonioTextBlockRecord, AntonioTextBlock } from '../Shared/AntonioTextBlock'
import { type AppDownloadCtaRecord, AppDownloadCta } from '../Shared/AppDownloadCta'
import { type CtaRecord, Cta } from '../Shared/CtaCollectionBlock'
import { type InlineAvatarRecord, InlineAvatarBlock } from '../Shared/InlineAvatarBlock'
import { type QuoteWithAuthorRecord, QuoteWithAuthorBlock } from '../Shared/QuoteWithAuthorBlock'
import { VideoBlock } from '../Video'
import { type EmailCaptureRecord, EmailCaptureBlock } from './EmailCapture'
import { type RatingRecord, RatingBlock } from './Rating'
import { SimpleImageBlock } from './SimpleImage'
import { processTypographyMarkdown } from './Typography'

const StyledPaddedWrapper = styled.div<{ $padding?: DSGridSpacing }>`
  ${({ $padding }) => $padding && `padding: var(--grid-${$padding});`}
`

export const DSOverrideStyles = styled.div<{ $baseSize: 200 | 300 | 400 }>`
  p:empty {
    height: var(--grid-24);
    display: block;
  }
  strong,
  b {
    font-weight: var(--font-sans-serif-semibold-weight);
    font-family: var(--font-sans-serif-semibold), var(--font-fallback-sans-serif);
  }

  /* The DS serif heading variants restyle <em> as the Nantes-Italic accent
     (font-style: normal + serif family). Rich-text content wants plain italics
     of the running font instead, so neutralise that swap here. Doubled class
     (&&) outranks the DS's .class em rule regardless of injection order. */
  && em {
    font-style: italic;
    font-family: inherit;
    font-weight: inherit;
  }

  // TODO: Move this to Design System - create lists and render them in renderNodeRule
  ul,
  ol {
    margin: 0;
    padding-left: 20px;
  }
  li {
    margin: ${(props) =>
        props.$baseSize === 200 ? 'var(--grid-8)' : props.$baseSize === 300 ? 'var(--grid-12)' : 'var(--grid-16)'}
      0;
  }
`

const fragment = `
  fragment RichTextBlockFragment on RichTextRecord {
    __typename
    id
    dataTestId
    baseSize
    baseWeight
    textAlign
    textAlignMobile
    colorScheme
    padding
    surfaceColor
    isRounded
    content {
      value
      blocks {
        __typename
        ... on SpacingRecord {
          id
          size
          mobileSize
        }
        ... on EyebrowRecord {
          id
          text
        }
        ... on VideoRecord {
            id
            videoUrl {
                providerUid
                provider
                title
            }
            width
        }
        ... on CtaRecord {
          id
          label
          url
          newTab
          variant
          colorScheme
          width
          dataTestId
        }
        ... on ListWithIconRecord {
          id
          gap
          items {
            id
            text
          }
        }
        ... on TextAndIconRecord {
          id
          text
          text2
          iconPosition
          alignment
          icon {
            id
            name
            size
            color
          }
        }
        ... on SimpleImageRecord {
          id
          image {
            ${responsiveImageBlock()}
          }
          imageMobile {
            ${responsiveImageBlock()}
          }
          rounded
          alignment
        }
        ... on InlineAvatarRecord {
          ...InlineAvatarBlockFragment
        }
        ... on QuoteWithAuthorRecord {
          ...QuoteWithAuthorBlockFragment
        }
        ... on RatingRecord {
          ...RatingBlockFragment
        }
        ... on EmailCaptureRecord {
          ...EmailCaptureBlockFragment
        }
        ... on AntonioTextRecord {
          ...AntonioTextBlockFragment
        }
        ... on AppDownloadCtaRecord {
          ...AppDownloadCtaFragment
        }
      }
    }
  }
  ${EmailCaptureBlock.fragment}
  ${RatingBlock.fragment}
  ${InlineAvatarBlock.fragment}
  ${QuoteWithAuthorBlock.fragment}
  ${AntonioTextBlock.fragment}
  ${AppDownloadCta.fragment}
`
enum BaseSize {
  small = 200,
  default = 300,
  large = 400,
}

enum BaseWeight {
  light = 'light',
  regular = 'regular',
  semiBold = 'semiBold',
  book = 'book',
}

type SpacingRecord = {
  __typename: 'SpacingRecord'
  id: string
  size: DSGridSpacing
  mobileSize: DSGridSpacing
}

type EyebrowRecord = {
  __typename: 'EyebrowRecord'
  id: string
  text: string
}

type ListWithIconRecord = {
  __typename: 'ListWithIconRecord'
  id: string
  gap?: DSGridSpacing
  items?: { id: string; text: string }[]
}

type TextAndIconRecord = {
  __typename: 'TextAndIconRecord'
  id: string
  text?: string
  text2?: string
  iconPosition?: 'left' | 'right'
  alignment?: DSFlexAlignItems
  icon: { name: DSIconName; size: DSIconSize; color?: DSIconColorType }
}

/** The blocks a `RichTextRecord`'s structured text can embed — one per `... on` in the fragment above. */
export type RichTextInnerBlockRecord =
  | AntonioTextBlockRecord
  | AppDownloadCtaRecord
  | CtaRecord
  | EmailCaptureRecord
  | EyebrowRecord
  | InlineAvatarRecord
  | ListWithIconRecord
  | QuoteWithAuthorRecord
  | RatingRecord
  | (SimpleImageRecord & { id: string }) // The fragment always selects `id`, which `SimpleImageRecord` leaves optional.
  | SpacingRecord
  | TextAndIconRecord
  | VideoRecord

export type RichTextBlockRecord = {
  __typename: 'RichTextRecord'
  id: string
  baseSize: BaseSize
  baseWeight: BaseWeight
  textAlign: DSTextAlignType
  textAlignMobile: DSTextAlignType
  colorScheme: DSTextColorType
  content: StructuredTextGraphQlResponse<RichTextInnerBlockRecord>
  surfaceColor?: DSSurfaceColorType
  padding?: DSGridSpacing
  isRounded?: boolean
  dataTestId?: string
}

const getHeadingVariant = (level: number, baseSize: BaseSize) => {
  switch (level) {
    case 1:
      return baseSize === BaseSize.small
        ? 'fluid-heading-800'
        : baseSize === BaseSize.default
          ? 'fluid-heading-900'
          : 'fluid-display-1000alt'
    case 2:
      return 'fluid-heading-800'
    case 3:
      return 'fluid-heading-700'
    case 4:
      return 'fluid-heading-600'
    case 5:
      return 'fluid-heading-500'
    default:
      return 'fluid-heading-500'
  }
}

const renderBlock = (
  { record }: RenderBlockContext<RichTextInnerBlockRecord>,
  {
    baseSize,
    baseWeight,
    textAlign,
    textAlignMobile,
    colorScheme,
  }: {
    baseSize: BaseSize
    baseWeight: BaseWeight
    textAlign: DSTextAlignType
    textAlignMobile: DSTextAlignType
    colorScheme: DSTextColorType
  },
): ReactElement | null => {
  switch (record.__typename) {
    case 'SpacingRecord':
      return <DSSpacer direction="vertical" size={record.size} breakAt={{ m: record.mobileSize }} key={record.id} />
    case 'CtaRecord':
      return (
        <DSFlex
          justifyContent={textAlign === 'center' ? 'center' : textAlign === 'right' ? 'flex-end' : 'flex-start'}
          breakConfig={{
            breakAt: {
              breakPoint: 'm',
              direction: 'column',
              justifyContent:
                textAlignMobile === 'center' ? 'center' : textAlignMobile === 'right' ? 'flex-end' : 'flex-start',
            },
            maxViewportDirection: 'row',
          }}
          key={record.id}
        >
          <Cta key={record.id} record={record} />
        </DSFlex>
      )
    case 'ListWithIconRecord': {
      return (
        <>
          {record.items?.map((item) => (
            <div key={item.id}>
              <DSTextAndIcon
                key={item.id}
                icon={{
                  name: 'tick-in-star',
                  position: 'left',
                  color: 'brand',
                  size: baseSize === BaseSize.small ? 16 : 24,
                }}
                alignment="flex-start"
                variant={`fluid-paragraph-${baseSize}`}
                weight={baseWeight}
                color={colorScheme}
              >
                {processTypographyMarkdown({
                  content: item.text,
                  variant: `fluid-paragraph-${baseSize}`,
                  weight: baseWeight,
                })}
              </DSTextAndIcon>
              <DSSpacer direction="vertical" size={record.gap || 16} />
            </div>
          ))}
        </>
      )
    }
    case 'TextAndIconRecord':
      return (
        <DSFlex
          direction="row"
          justifyContent={textAlign === 'center' ? 'center' : textAlign === 'right' ? 'flex-end' : 'flex-start'}
        >
          <DSTextAndIcon
            icon={{ ...record.icon, position: record.iconPosition }}
            variant={`fluid-paragraph-${baseSize}`}
            weight={baseWeight}
            color={colorScheme}
            alignment={record.alignment || 'center'}
          >
            {processTypographyMarkdown({
              content: record.text || record.text2 || '',
              variant: `fluid-paragraph-${baseSize}`,
              weight: baseWeight,
            })}
          </DSTextAndIcon>
        </DSFlex>
      )
    case 'SimpleImageRecord':
      return <SimpleImageBlock record={record} />
    case 'InlineAvatarRecord':
      return <InlineAvatarBlock record={record} />
    case 'QuoteWithAuthorRecord':
      return <QuoteWithAuthorBlock record={record} />
    case 'VideoRecord':
      return <VideoBlock record={record} />
    case 'EyebrowRecord':
      return (
        <DSText variant="fluid-label-100" weight={baseWeight} color={colorScheme}>
          {record.text.toUpperCase()}
        </DSText>
      )
    case 'EmailCaptureRecord':
      return <EmailCaptureBlock record={record} />
    case 'RatingRecord':
      return <RatingBlock record={record} />
    case 'AntonioTextRecord':
      return <AntonioTextBlock record={record} />
    case 'AppDownloadCtaRecord':
      return (
        <DSFlex
          justifyContent={textAlign === 'center' ? 'center' : textAlign === 'right' ? 'flex-end' : 'flex-start'}
          breakConfig={{
            breakAt: {
              breakPoint: 'm',
              direction: 'column',
              alignItems:
                textAlignMobile === 'center' ? 'center' : textAlignMobile === 'right' ? 'flex-end' : 'flex-start',
            },
            maxViewportDirection: 'row',
          }}
          key={record.id}
        >
          <AppDownloadCta record={record} />
        </DSFlex>
      )
    default:
      return null
  }
}

export const RichTextBlock: FragmentComponent<{}, RichTextBlockRecord> = ({ record }) => {
  return (
    <DSSurface surfaceColor={record.surfaceColor} radius={record.isRounded ? 16 : undefined}>
      <StyledPaddedWrapper $padding={record.padding}>
        <DSOverrideStyles $baseSize={record.baseSize || 300} data-testid={record.dataTestId || 'rich-text-block'}>
          <StructuredText
            data={record.content}
            customNodeRules={[
              renderNodeRule(isThematicBreak, ({ key }) => <DSDivider key={key} />),
              renderNodeRule(isLink, ({ node, children, key }) => {
                const metaAttributes = node.meta?.reduce<Record<string, string>>((acc, meta) => {
                  acc[meta.id] = meta.value
                  return acc
                }, {})
                return (
                  <TrackedInlineLink href={node.url} {...metaAttributes} key={key}>
                    {children}
                  </TrackedInlineLink>
                )
              }),
              renderNodeRule(isParagraph, ({ children, key }) => {
                return (
                  <DSText
                    variant={`fluid-paragraph-${record.baseSize}`}
                    as="p"
                    weight={record.baseWeight}
                    key={key}
                    align={record.textAlign}
                    color={record.colorScheme}
                    breakAt={{ breakPoint: 'm', align: record.textAlignMobile }}
                  >
                    {children}
                  </DSText>
                )
              }),
              renderNodeRule(isHeading, ({ children, key, node }) => {
                return (
                  <DSText
                    variant={getHeadingVariant(node.level, record.baseSize)}
                    as={`h${node.level}`}
                    weight={record.baseWeight}
                    key={key}
                    align={record.textAlign}
                    color={record.colorScheme}
                    breakAt={{ breakPoint: 'm', align: record.textAlignMobile }}
                  >
                    {children}
                  </DSText>
                )
              }),
            ]}
            renderBlock={(innerRecord) => {
              return renderBlock(innerRecord, {
                baseSize: record.baseSize,
                baseWeight: record.baseWeight,
                textAlign: record.textAlign,
                textAlignMobile: record.textAlignMobile,
                colorScheme: record.colorScheme,
              })
            }}
          />
        </DSOverrideStyles>
      </StyledPaddedWrapper>
    </DSSurface>
  )
}

RichTextBlock.fragment = fragment
RichTextBlock.recordName = 'RichTextRecord'
